import { ComponentProps, forwardRef, useRef } from "react";
import { cn } from "@/lib/utils";

export type CardVariant = "default" | "elevated" | "subtle" | "glass";

export interface CardProps extends ComponentProps<"div"> {
  /**
   * Visual weight tier. `elevated` for primary hero / call-to-action
   * surfaces, `default` for KPIs and content blocks, `subtle` for
   * supporting / supplementary cards.
   *
   * Picking the right tier is what creates page hierarchy — when every
   * card looks the same (the old behavior), the eye has no anchor.
   */
  variant?: CardVariant;
  /**
   * Opt-in hover-lift + active press for cards that represent a
   * clickable item (workout cards, program packs, list rows). Static
   * cards leave this off so dashboards don't twitch on cursor move.
   */
  interactive?: boolean;
}

const VARIANT_STYLES: Record<CardVariant, string> = {
  default:
    "glass-card border border-line/40 shadow-sm",
  elevated:
    "glass-card border border-line/60 shadow-md ring-1 ring-brand/5",
  subtle:
    "bg-muted/5 border border-line/20",
  // Future Kinetic glass — opt-in heavy blur + translucent fill +
  // inner light edge (via the `.glass` class hook in index.css).
  // Use for hero / nav / stat surfaces, NOT long scroll lists.
  glass:
    "glass-card glass border border-line/40 shadow-md",
};

const INTERACTIVE_STYLES =
  "cursor-pointer transition-all duration-200 ease-out " +
  "hover:-translate-y-0.5 hover:shadow-lg hover:border-brand/30 " +
  "active:translate-y-0 active:scale-[0.99]";

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className = "", variant = "default", interactive = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-[var(--radius-lg)]",
        VARIANT_STYLES[variant],
        interactive && INTERACTIVE_STYLES,
        className,
      )}
      {...props}
    />
  )
);
Card.displayName = "Card";

export interface ButtonProps extends ComponentProps<"button"> {
  variant?: "primary" | "secondary" | "ghost" | "kinetic";
  /** Opt-in: the button subtly drifts toward the cursor on hover.
   *  Auto-disabled on coarse pointers (touch) and when `disabled`. */
  magnetic?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "primary", className = "", magnetic = false, disabled, onMouseMove, onMouseLeave, ...props }, ref) => {
    const baseStyles = "inline-flex items-center justify-center gap-2 min-h-11 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand transition-all duration-200 ease-out active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed";

    const variantStyles = {
      primary:
        "text-primary-foreground bg-gradient-to-b from-brand to-brand-700 shadow-md hover:shadow-lg hover:-translate-y-px hover:brightness-105",
      // Marquee CTA, theme-conditional so text contrast holds in both:
      //  * light (Pulse): solid electric-blue + white text + brand glow
      //  * dark (Future Kinetic): bright cyan→periwinkle gradient with
      //    deep-navy text (--brand-50 = #002b75) + cyan glow. Navy on
      //    cyan = 8.45 AA, navy on periwinkle = high contrast.
      kinetic:
        "hover:brightness-110 hover:-translate-y-px " +
        "bg-brand text-white glow-brand " +
        "dark:bg-transparent dark:gradient-kinetic dark:text-[hsl(var(--brand-50))] dark:glow-cyan",
      secondary:
        "text-foreground border border-border bg-card hover:bg-accent hover:border-foreground/20",
      ghost: "text-muted hover:text-foreground hover:bg-accent",
    };

    // Magnetic hover: track cursor and translate the button toward it.
    // Disabled on coarse pointers (touch) and when the button is
    // disabled. We compose with any user-supplied onMouseMove/Leave
    // rather than replacing them.
    const localRef = useRef<HTMLButtonElement | null>(null);
    const setRefs = (el: HTMLButtonElement | null) => {
      localRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = el;
    };
    const isCoarse =
      typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;
    const magneticOn = magnetic && !isCoarse && !disabled;

    const handleMove = (e: React.MouseEvent<HTMLButtonElement>) => {
      onMouseMove?.(e);
      if (!magneticOn) return;
      const el = localRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left - r.width / 2) * 0.3;
      const y = (e.clientY - r.top - r.height / 2) * 0.4;
      el.style.transform = `translate(${x}px, ${y}px)`;
    };
    const handleLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
      onMouseLeave?.(e);
      if (!magneticOn) return;
      if (localRef.current) localRef.current.style.transform = "";
    };

    return (
      <button
        ref={setRefs}
        disabled={disabled}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        className={cn(
          baseStyles,
          variantStyles[variant],
          magneticOn && "will-change-transform",
          className,
        )}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export const Tag = ({ children, className = "" }: { children: string; className?: string }) => (
  <span className={cn("px-2 py-0.5 text-xs rounded-full border border-line text-muted bg-muted/40", className)}>
    {children}
  </span>
);

export const Skeleton = ({ className = "" }: { className?: string }) => (
  // Animated shimmer sweep replaces the static animate-pulse for a
  // more premium loading feel. Gradient slides across the element via
  // background-position; respects both light + dark themes by tinting
  // through black/white at low alpha.
  <div
    className={cn(
      "rounded animate-shimmer",
      "bg-[length:200%_100%]",
      "bg-gradient-to-r from-black/5 via-black/10 to-black/5",
      "dark:from-white/10 dark:via-white/20 dark:to-white/10",
      className,
    )}
  />
);
